import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Newspaper, Plus, X, ExternalLink, RefreshCw, Sparkles, Share2, Clock, Link2, Mail, Linkedin, MessageCircle, Eye, Users, Lock } from "lucide-react";
import { Skeleton } from "@/components/Skeleton";

const relativeTime = (iso) => {
    if (!iso) return "";
    const t = Date.parse(iso);
    if (!t) return iso;
    const diff = Date.now() - t;
    const m = Math.round(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    if (d < 30) return `${d}d ago`;
    return new Date(t).toLocaleDateString();
};

// URL-safe base64 codec for embedding the publisher link inside an
// Odyssey share URL. We use base64url (no + or /) so the link survives
// being pasted into Slack, WhatsApp, email, etc. without URL-encoding.
const encodeShareLink = (link) => {
    try { return btoa(unescape(encodeURIComponent(link))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, ""); }
    catch { return ""; }
};
const decodeShareLink = (token) => {
    try {
        const padded = token.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((token.length + 3) % 4);
        return decodeURIComponent(escape(atob(padded)));
    } catch { return ""; }
};

// Builds the legacy `?a=<base64url>` Odyssey deeplink. We keep it as a
// synchronous fallback for the moment the ShareDialog mounts (before the
// 7-char short code lands), and so old links shared in the past still
// resolve when somebody clicks them.
export const buildShareUrl = (link) => {
    const tok = encodeShareLink(link);
    if (!tok) return link;
    return `${window.location.origin}/news?a=${tok}`;
};

// Module-level cache for short codes: same link → same code, so we don't
// re-POST every time someone re-opens the share dialog on the same article.
const _shortCodeCache = new Map();
// The shareable URL points at our backend `/api/news/s/{code}` endpoint —
// it emits OpenGraph tags (so WhatsApp / Slack / LinkedIn show a rich
// preview with the article image, headline + source) and then redirects
// human visitors into the SPA via meta-refresh + JS.
const buildShortShareUrl = (code) => `${window.location.origin}/api/news/s/${code}`;
const fetchOrCreateShortCode = async (item) => {
    if (!item?.link) return null;
    const cached = _shortCodeCache.get(item.link);
    if (cached) return cached;
    try {
        const r = await api.post("/news/share", {
            link: item.link,
            title: item.title || "",
            source: item.source || "",
            // Pass image + summary so link-preview crawlers (WhatsApp et al.)
            // can render a rich card without us needing a second scrape pass.
            image_url: item.image || "",
            summary: item.summary || "",
        });
        const code = r.data?.code;
        if (code) {
            _shortCodeCache.set(item.link, code);
            return code;
        }
    } catch { /* fall back to long URL */ }
    return null;
};

// Cross-environment copy. The modern Clipboard API throws in three
// places we actually hit in the wild:
//   1. Inside iframes without `allow="clipboard-write"` (Emergent preview)
//   2. Older Safari (< 13.4)
//   3. Some MDM-locked enterprise browsers
// So we cascade: Clipboard API → execCommand('copy') → a tiny
// "long-press to copy" prompt dialog as a guaranteed last resort.
const copyToClipboard = async (text) => {
    // 1) Clipboard API — works ~90% of the time when it's available.
    if (navigator.clipboard?.writeText) {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch { /* fall through to execCommand */ }
    }
    // 2) execCommand('copy') — legacy but iframe-friendly. We mount a
    // throwaway textarea, select its contents, run the command, remove.
    try {
        const ta = document.createElement("textarea");
        ta.value = text;
        // Off-screen but still in-flow so iOS Safari will select it.
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.top = "0";
        ta.style.left = "0";
        ta.style.opacity = "0";
        ta.style.pointerEvents = "none";
        document.body.appendChild(ta);
        ta.select();
        ta.setSelectionRange(0, text.length);
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (ok) return true;
    } catch { /* fall through */ }
    return false;
};

const shareArticle = async (item) => {
    const shareUrl = buildShareUrl(item.link);
    // Web Share API on mobile / supported desktop — best UX
    if (navigator.share) {
        try {
            await navigator.share({
                title: item.title,
                text: `${item.source ? `${item.source} · ` : ""}${item.title}`,
                url: shareUrl,
            });
            return;
        } catch (e) {
            // User cancelled — silently swallow
            if (e?.name === "AbortError") return;
        }
    }
    const copied = await copyToClipboard(shareUrl);
    if (copied) {
        toast.success("Odyssey link copied — paste in Slack/email to share");
        return;
    }
    // Final fallback — pop a prompt so the URL is at least selectable. On
    // sandboxed iframes this is the most reliable way for the user to grab
    // the link without us having permission to write to their clipboard.
    try {
        window.prompt("Copy this Odyssey share link:", shareUrl);
    } catch {
        toast.error("Couldn't copy — try right-click → Copy on the share button");
    }
};

// Stable colourful gradient seed when there's no thumbnail.
const PALETTES = [
    "linear-gradient(135deg, #2EB4AC 0%, #1F7F79 100%)",
    "linear-gradient(135deg, #6366f1 0%, #1e1b4b 100%)",
    "linear-gradient(135deg, #f43f5e 0%, #9f1239 100%)",
    "linear-gradient(135deg, #f59e0b 0%, #b45309 100%)",
    "linear-gradient(135deg, #06b6d4 0%, #0e7490 100%)",
    "linear-gradient(135deg, #8b5cf6 0%, #5b21b6 100%)",
    "linear-gradient(135deg, #ec4899 0%, #831843 100%)",
];
const paletteFor = (key) => {
    let h = 0;
    for (let i = 0; i < (key || "").length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
    return PALETTES[Math.abs(h) % PALETTES.length];
};

/**
 * Article card with optional image. `size` controls visual hierarchy.
 *   - "hero"   : full-bleed image, large headline overlay
 *   - "medium" : same overlay style at smaller scale
 *   - "row"    : compact list row, image to the left, no overlay
 */
// Google's favicon CDN gives us a guaranteed thumbnail for any domain
// (256px is the largest tier — perfect for our centered-logo fallback).
const faviconUrl = (domain) => domain
    ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=256`
    : "";

// Fire-and-forget view receipt. Bumps the per-user/per-article counter
// and the global "who's read this" log on every Read click. We do NOT
// await — the browser is already navigating to the publisher in a new
// tab, the receipt is best-effort.
const trackArticleView = (item) => {
    if (!item?.link) return;
    try {
        api.post("/news/views", {
            link: item.link,
            source: item.source || "",
            title: item.title || "",
            topic: item.topic || "",
            publisher_domain: item.publisher_domain || "",
        }).catch(() => { /* silent */ });
    } catch { /* no-op */ }
};

export const NewsCard = ({ item, size = "medium", fill = false, onShare, onShowViewers }) => {
    const fallback = paletteFor(item.source || item.title || "");
    const fav = faviconUrl(item.publisher_domain || "");
    const handleShareClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onShare) onShare(item);
        else shareArticle(item);
    };
    const handleViewersClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        onShowViewers?.(item);
    };
    const viewCount = item.view_count || 0;
    if (size === "row") {
        return (
            <a
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackArticleView(item)}
                data-testid={`news-card-${(item.link || "").slice(-12)}`}
                className="group flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 transition-colors"
            >
                <span
                    className="h-14 w-14 rounded-md shrink-0 overflow-hidden flex items-center justify-center text-white font-bold text-lg"
                    style={item.image ? {} : { background: fallback }}
                >
                    {item.image
                        ? <img src={item.image} alt="" className="h-full w-full object-cover" loading="lazy" />
                        : fav
                            ? <img src={fav} alt="" className="h-8 w-8 rounded-sm" loading="lazy" />
                            : (item.source || "?").charAt(0).toUpperCase()}
                </span>
                <span className="flex-1 min-w-0">
                    <span className="block text-[10px] uppercase tracking-[0.16em] text-[#25908A] font-semibold truncate">
                        {item.source}<span className="text-slate-400 font-medium normal-case tracking-normal ml-1">· {relativeTime(item.published)}</span>
                    </span>
                    <span className="block text-sm font-semibold text-slate-900 leading-snug line-clamp-2 group-hover:text-[#1F7F79] mt-0.5">{item.title}</span>
                </span>
                {viewCount > 0 && (
                    <button
                        type="button"
                        onClick={handleViewersClick}
                        data-testid={`news-viewers-${(item.link || "").slice(-12)}`}
                        aria-label={`${viewCount} ${viewCount === 1 ? "reader" : "readers"}`}
                        title={`${viewCount} ${viewCount === 1 ? "teammate has" : "teammates have"} read this`}
                        className="inline-flex items-center gap-1 h-7 px-2 rounded-full bg-teal-50/70 border border-teal-100 text-[#1F7F79] text-[11px] font-bold hover:bg-teal-50 hover:border-[#2EB4AC] transition-colors"
                    >
                        <Eye className="h-3 w-3" /> {viewCount}
                    </button>
                )}
                <button
                    type="button"
                    onClick={handleShareClick}
                    data-testid={`news-share-${(item.link || "").slice(-12)}`}
                    aria-label="Share via Odyssey link"
                    className="opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7 rounded-full border border-slate-200 text-slate-500 inline-flex items-center justify-center hover:border-[#2EB4AC] hover:text-[#25908A]"
                >
                    <Share2 className="h-3.5 w-3.5" />
                </button>
            </a>
        );
    }
    const isHero = size === "hero";
    // When `fill` is true the parent dictates height (e.g., grid-rows in
    // the right rail of the magazine grid) — drop the aspect ratio so
    // the card stretches to fill the cell instead of leaving dead space.
    //
    // For hero + standalone medium cards we use explicit heights instead
    // of `aspect-ratio` because aspect-ratio is bidirectional: when
    // `max-h` clips the height the browser shrinks the *width* in lock-
    // step, leaving a fat empty cell on wide viewports. Fixed heights +
    // `w-full` keep the card spanning its grid column edge-to-edge.
    const sizeClasses = isHero
        ? "w-full h-[300px] sm:h-[360px] lg:h-[400px]"
        : (fill ? "h-full w-full min-h-[170px]" : "w-full h-[180px] sm:h-[200px]");
    return (
        <a
            href={item.link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackArticleView(item)}
            data-testid={`news-card-${(item.link || "").slice(-12)}`}
            className={`group relative block rounded-2xl overflow-hidden transition-transform duration-300 hover:-translate-y-0.5 shadow-sm hover:shadow-xl ${sizeClasses}`}
            style={item.image ? {} : { background: fallback }}
        >
            {item.image ? (
                <img
                    src={item.image}
                    alt=""
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
            ) : fav ? (
                // Favicon-on-gradient fallback — guarantees every card
                // shows a visual preview even when the publisher doesn't
                // expose og:image. Sized large and centered with a soft
                // backdrop ring so it reads as a brand mark.
                <span aria-hidden className="absolute inset-0 flex items-center justify-center">
                    <span
                        className={`rounded-2xl bg-white/15 backdrop-blur-sm border border-white/25 flex items-center justify-center shadow-[0_8px_24px_-4px_rgba(0,0,0,0.35)] ${isHero ? "h-24 w-24" : "h-16 w-16"}`}
                    >
                        <img
                            src={fav}
                            alt=""
                            loading="lazy"
                            className={isHero ? "h-14 w-14" : "h-10 w-10"}
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                    </span>
                </span>
            ) : null}
            {/* Bottom-up dark gradient so the headline always reads */}
            <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-transparent" />
            <span className="absolute top-3 left-3 inline-flex items-center px-2 py-0.5 rounded-full bg-white/15 backdrop-blur-sm text-white text-[10px] font-semibold uppercase tracking-[0.18em] border border-white/20">
                {item.source}
            </span>
            {/* Share button + viewers pill — clustered top-right.
                Both stop the underlying anchor's navigate-to-publisher. */}
            <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5">
                {viewCount > 0 && (
                    <button
                        type="button"
                        onClick={handleViewersClick}
                        data-testid={`news-viewers-${(item.link || "").slice(-12)}`}
                        aria-label={`${viewCount} ${viewCount === 1 ? "reader" : "readers"}`}
                        className="inline-flex items-center gap-1 h-8 px-2.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/25 text-white text-[11px] font-bold hover:bg-white/30 hover:scale-105 active:scale-95 transition-all"
                    >
                        <Eye className="h-3 w-3" /> {viewCount}
                    </button>
                )}
                <button
                    type="button"
                    onClick={handleShareClick}
                    data-testid={`news-share-${(item.link || "").slice(-12)}`}
                    aria-label="Share via Odyssey link"
                    className="h-8 w-8 rounded-full bg-white/15 backdrop-blur-sm border border-white/25 text-white inline-flex items-center justify-center hover:bg-white/30 hover:scale-110 active:scale-95 transition-all"
                >
                    <Share2 className="h-3.5 w-3.5" />
                </button>
            </div>
            <span className="absolute bottom-0 left-0 right-0 p-4">
                <span className={`block font-heading font-bold text-white leading-tight ${isHero ? "text-2xl line-clamp-3" : "text-base line-clamp-3"}`}>
                    {item.title}
                </span>
                <span className="mt-2 flex items-center justify-between gap-2 text-white/80 text-[11px] font-medium">
                    <span>{relativeTime(item.published)}</span>
                    <span className="inline-flex items-center gap-1 opacity-80 group-hover:opacity-100">Read <ExternalLink className="h-3 w-3" /></span>
                </span>
            </span>
        </a>
    );
};

const News = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [topics, setTopics] = useState([]);
    // Topics returned by the backend that the user cannot remove (e.g.
    // "ecommerce" — pinned for everyone since it's our org's core beat).
    const [pinnedTopics, setPinnedTopics] = useState(["ecommerce"]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [newTopic, setNewTopic] = useState("");
    const [savingTopics, setSavingTopics] = useState(false);
    const [activeTopic, setActiveTopic] = useState("__all__");
    const [lastFetched, setLastFetched] = useState(null);
    // The article whose share menu is currently open. One global dialog
    // for the whole page — simpler than a popover-per-card and side-steps
    // the card's `overflow-hidden` clipping its own share popover.
    const [shareTarget, setShareTarget] = useState(null);
    // Same single-instance pattern for the "who read this article" panel.
    const [viewersTarget, setViewersTarget] = useState(null);
    // Force the "Updated 2m ago" pill to recompute every minute without
    // re-fetching the feed.
    const [, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick((v) => v + 1), 60000);
        return () => clearInterval(id);
    }, []);

    const load = async () => {
        setRefreshing(true);
        try {
            const r = await api.get("/news?limit=30");
            setItems(r.data?.items || []);
            setTopics(r.data?.topics || []);
            setLastFetched(r.data?.last_fetched_at || null);
            // Refresh pinned list from /news/prefs (one extra cheap call)
            try {
                const p = await api.get("/news/prefs");
                if (Array.isArray(p.data?.pinned)) setPinnedTopics(p.data.pinned);
            } catch { /* keep default */ }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load news");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };
    useEffect(() => { load(); }, []);

    // Deeplink: short code `?s=<code>` (preferred) or legacy
    // `?a=<base64url(link)>` (kept so old shared URLs still resolve). The
    // first one pins the shared article to the top + auto-scrolls it into
    // view. We resolve it against the loaded items so the headline /
    // image / source decoration is identical to every other card. If the
    // article isn't in the user's current feed (different topic / fell off
    // the window), we synthesize a minimal card from the URL alone — the
    // user still gets to it via the Read CTA.
    const sharedCode = searchParams.get("s") || "";
    const sharedToken = searchParams.get("a") || "";
    const [resolvedShortLink, setResolvedShortLink] = useState({ code: "", link: "", title: "", source: "" });
    useEffect(() => {
        let alive = true;
        if (!sharedCode) { setResolvedShortLink({ code: "", link: "", title: "", source: "" }); return; }
        if (sharedCode === resolvedShortLink.code) return; // already resolved
        (async () => {
            try {
                const r = await api.get(`/news/share/${encodeURIComponent(sharedCode)}`);
                if (!alive) return;
                setResolvedShortLink({
                    code: sharedCode,
                    link: r.data?.link || "",
                    title: r.data?.title || "",
                    source: r.data?.source || "",
                });
            } catch {
                if (alive) setResolvedShortLink({ code: sharedCode, link: "", title: "", source: "" });
            }
        })();
        return () => { alive = false; };
    }, [sharedCode]);
    const sharedLink = useMemo(() => {
        if (sharedCode) return resolvedShortLink.link || "";
        return sharedToken ? decodeShareLink(sharedToken) : "";
    }, [sharedCode, sharedToken, resolvedShortLink]);
    const sharedArticle = useMemo(() => {
        if (!sharedLink) return null;
        const match = items.find((it) => it.link === sharedLink);
        if (match) return match;
        // Fallback synthetic article when the shared link isn't in feed
        let domain = "";
        try { domain = new URL(sharedLink).hostname.replace(/^www\./, ""); } catch { /* ignore */ }
        return {
            link: sharedLink,
            title: resolvedShortLink.title || "Shared article",
            source: resolvedShortLink.source || domain || "external link",
            published: null,
            image: "",
            publisher_domain: domain,
        };
    }, [sharedLink, items, resolvedShortLink]);
    const dismissShared = () => {
        const next = new URLSearchParams(searchParams);
        next.delete("a");
        next.delete("s");
        setSearchParams(next, { replace: true });
    };

    const saveTopics = async (next) => {
        setSavingTopics(true);
        try {
            const r = await api.put("/news/prefs", { topics: next });
            setTopics(r.data?.topics || next);
            toast.success("Topics updated");
            const f = await api.get("/news?limit=30");
            setItems(f.data?.items || []);
            setLastFetched(f.data?.last_fetched_at || null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save");
        } finally { setSavingTopics(false); }
    };
    const addTopic = () => {
        const t = (newTopic || "").trim();
        if (!t) return;
        if (topics.includes(t.toLowerCase())) { toast.info("Already added"); setNewTopic(""); return; }
        setNewTopic("");
        saveTopics([...topics, t].slice(0, 10));
    };
    const removeTopic = (t) => {
        if (pinnedTopics.includes(t)) {
            toast.info("This topic is pinned by default.");
            return;
        }
        if (topics.length <= 1) { toast.info("Keep at least one topic."); return; }
        saveTopics(topics.filter((x) => x !== t));
    };

    // Filter (in-app) by selected topic chip without re-fetching, then
    // enforce strict recency order client-side too — the backend already
    // sorts newest-first but this guarantees hero/rail/rest all follow
    // the same ordering even if a cache row is ever served stale-mixed.
    const visible = useMemo(() => {
        const base = activeTopic === "__all__"
            ? items
            : items.filter((it) => it.topic === activeTopic);
        const ts = (it) => {
            const p = it?.published;
            if (!p) return 0;
            const n = Date.parse(p);
            return Number.isFinite(n) ? n : 0;
        };
        return [...base].sort((a, b) => ts(b) - ts(a));
    }, [items, activeTopic]);

    // Magazine grid layout:
    //  - Row 1: hero (8 cols) + 2 medium cards stacked on the right (4 cols)
    //           Right rail uses `grid-rows-2` + `h-full` so the two cards
    //           together exactly mirror the hero's height — no dead space.
    //  - Row 2: a 4-up rail of equal medium cards. We pad it from `rest`
    //           if there aren't enough featured items so we never leave
    //           an empty grid slot.
    //  - Row 3+: every remaining story as a compact list row.
    const hero = visible[0];
    const rightRail = visible.slice(1, 3);
    const mediumRowRaw = visible.slice(3, 7);
    // Top up the 4-up row from `rest` (the compact list) so the row is
    // never partially filled. Whatever we promote here is removed from
    // the compact list to avoid duplicate cards.
    const mediumRowPad = Math.max(0, 4 - mediumRowRaw.length);
    const restSourceStart = 7;
    const mediumRow = mediumRowRaw.concat(visible.slice(restSourceStart, restSourceStart + mediumRowPad));
    const rest = visible.slice(restSourceStart + mediumRowPad);

    // Unique source names in the visible feed — feeds the marquee ticker.
    const tickerSources = useMemo(() => {
        const seen = new Map();
        for (const it of visible) {
            const s = (it.source || "").trim();
            if (!s || seen.has(s)) continue;
            seen.set(s, { name: s, domain: it.publisher_domain || "" });
        }
        return Array.from(seen.values()).slice(0, 30);
    }, [visible]);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-heading text-3xl font-bold text-slate-900 inline-flex items-center gap-2 capitalize">
                        <Newspaper className="h-7 w-7 text-[#2EB4AC]" />
                        {activeTopic === "__all__" ? "Top news" : `Top ${activeTopic} news`}
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">Latest stories first · India · refreshes every 30 min</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {/* Freshness pill — tells the user how stale the feed is
                        so they can decide whether to hit Refresh. Tooltip
                        shows the absolute timestamp for the precision-curious. */}
                    {lastFetched && (
                        <span
                            data-testid="news-last-fetched"
                            title={new Date(lastFetched).toLocaleString()}
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-slate-50 border border-slate-200 text-[11px] font-semibold text-slate-600"
                        >
                            <Clock className="h-3 w-3 text-[#2EB4AC]" />
                            Updated {relativeTime(lastFetched)}
                        </span>
                    )}
                    <button
                        onClick={load}
                        disabled={refreshing}
                        data-testid="news-refresh-btn"
                        className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-200 hover:border-[#2EB4AC] text-sm font-semibold text-slate-700 hover:text-[#25908A] transition-colors disabled:opacity-50"
                    >
                        <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
                        Refresh
                    </button>
                </div>
            </div>

            {/* Shared article hero — appears at the very top when arriving
                via a /news?a=... deeplink. Gives the recipient context
                BEFORE they bounce off to the publisher. */}
            {sharedArticle && (
                <SharedArticleHero
                    item={sharedArticle}
                    onDismiss={dismissShared}
                    onShare={() => setShareTarget(sharedArticle)}
                />
            )}

            {/* Live source ticker — auto-scrolling marquee of every
                publisher in the current view. Reminds users that the
                feed is sourced from many real publications, not one. */}
            {tickerSources.length > 1 && (
                <SourceTicker sources={tickerSources} />
            )}

            {/* Topic filter chips — magazine-style horizontal nav */}
            <div className="flex items-center gap-2 flex-wrap border-b border-slate-200 pb-3">
                <button
                    onClick={() => setActiveTopic("__all__")}
                    data-testid="topic-tab-all"
                    className={`text-sm font-semibold pb-2 -mb-3 px-1 transition-colors ${
                        activeTopic === "__all__"
                            ? "text-[#1F7F79] border-b-2 border-[#2EB4AC]"
                            : "text-slate-500 hover:text-slate-900"
                    }`}
                >
                    All
                </button>
                {topics.map((t) => {
                    const isPinned = pinnedTopics.includes(t);
                    return (
                    <span key={t} className="inline-flex items-center">
                        <button
                            onClick={() => setActiveTopic(t)}
                            data-testid={`topic-tab-${t}`}
                            className={`text-sm font-semibold pb-2 -mb-3 px-1 capitalize transition-colors ${
                                activeTopic === t
                                    ? "text-[#1F7F79] border-b-2 border-[#2EB4AC]"
                                    : "text-slate-500 hover:text-slate-900"
                            }`}
                        >
                            {t}
                        </button>
                        {isPinned ? (
                            // Pinned topics get a tiny lock icon instead of the
                            // remove (X) so users understand it's permanent.
                            <span
                                aria-label={`${t} is pinned`}
                                title="Pinned by default"
                                className="ml-1 text-slate-300"
                            >
                                <Lock className="h-3 w-3" />
                            </span>
                        ) : (
                            <button
                                onClick={() => removeTopic(t)}
                                disabled={savingTopics}
                                aria-label={`Remove ${t}`}
                                className="ml-1 text-slate-300 hover:text-rose-500"
                            >
                                <X className="h-3 w-3" />
                            </button>
                        )}
                    </span>
                    );
                })}
                <span className="inline-flex items-center gap-1 ml-2">
                    <input
                        type="text"
                        value={newTopic}
                        onChange={(e) => setNewTopic(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") addTopic(); }}
                        placeholder="add topic…"
                        maxLength={60}
                        data-testid="news-add-topic-input"
                        className="h-7 px-2.5 text-xs border border-slate-200 rounded-full focus:ring-2 focus:ring-[#2EB4AC] focus:border-[#2EB4AC] outline-none w-32"
                    />
                    <button
                        onClick={addTopic}
                        disabled={savingTopics || !newTopic.trim() || topics.length >= 10}
                        data-testid="news-add-topic-btn"
                        className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-[#2EB4AC] hover:bg-[#25908A] text-white disabled:opacity-40"
                        title={topics.length >= 10 ? "Up to 10 topics" : "Add topic"}
                    >
                        <Plus className="h-3.5 w-3.5" />
                    </button>
                </span>
                <span className="text-[10px] text-slate-400 ml-auto inline-flex items-center gap-1">
                    <Sparkles className="h-3 w-3" /> Try D2C · quick commerce · flipkart
                </span>
            </div>

            {/* Magazine grid */}
            {loading ? (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    <Skeleton className="lg:col-span-8 aspect-[16/11] rounded-2xl" />
                    <div className="lg:col-span-4 space-y-4">
                        <Skeleton className="aspect-[5/4] rounded-2xl" />
                        <Skeleton className="aspect-[5/4] rounded-2xl" />
                    </div>
                </div>
            ) : visible.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-lg p-10 text-center">
                    <Newspaper className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <div className="text-sm font-semibold text-slate-900">No stories yet</div>
                    <div className="text-xs text-slate-500 mt-1">Add a topic above to start your feed.</div>
                </div>
            ) : (
                <>
                    {/* Hero + right rail — heights matched so the rail
                        exactly mirrors the hero card, killing the gap
                        that used to sit under the right column. */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" data-testid="news-list">
                        {hero && (
                            <div className="lg:col-span-8">
                                <NewsCard item={hero} size="hero" onShare={setShareTarget} onShowViewers={setViewersTarget} />
                            </div>
                        )}
                        {rightRail.length > 0 && (
                            <div className="lg:col-span-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 lg:grid-rows-2 gap-4 h-full">
                                {rightRail.map((it) => <NewsCard key={it.link} item={it} size="medium" fill onShare={setShareTarget} onShowViewers={setViewersTarget} />)}
                            </div>
                        )}
                        {/* 4-up rail of equal medium cards — always full,
                            never an empty slot (we top up from `rest`). */}
                        {mediumRow.length > 0 && (
                            <div className="lg:col-span-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                {mediumRow.map((it) => <NewsCard key={it.link} item={it} size="medium" onShare={setShareTarget} onShowViewers={setViewersTarget} />)}
                            </div>
                        )}
                    </div>

                    {/* Remaining stories as compact rows */}
                    {rest.length > 0 && (
                        <div className="mt-6">
                            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold mb-2">More stories</div>
                            <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
                                {rest.map((it) => <NewsCard key={it.link} item={it} size="row" onShare={setShareTarget} onShowViewers={setViewersTarget} />)}
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* One global share dialog — opened by any card's share button */}
            {shareTarget && (
                <ShareDialog item={shareTarget} onClose={() => setShareTarget(null)} />
            )}
            {/* One global viewers dialog — opened by any card's "Read by N" pill */}
            {viewersTarget && (
                <ViewersDialog item={viewersTarget} onClose={() => setViewersTarget(null)} />
            )}
        </div>
    );
};

export default News;

/**
 * SourceTicker — auto-scrolling marquee of publisher names. We render
 * the source list TWICE end-to-end and animate the strip leftward by
 * -50%, which produces a seamless loop (when frame 1 reaches the end,
 * frame 2 is already in place). Pauses on hover so a curious user can
 * read a specific source without it sliding away.
 */
const SourceTicker = ({ sources }) => {
    const seq = [...sources, ...sources]; // duplicated for seamless loop
    return (
        <div
            data-testid="news-source-ticker"
            className="relative overflow-hidden bg-gradient-to-r from-slate-50 via-white to-slate-50 border border-slate-200 rounded-xl py-2.5"
        >
            {/* Edge fade so chips don't get sliced abruptly at the borders */}
            <div aria-hidden className="absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
            <div aria-hidden className="absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
            <div className="flex group">
                <div className="flex shrink-0 items-center gap-3 px-4 animate-news-ticker group-hover:[animation-play-state:paused]">
                    {seq.map((s, i) => (
                        <span
                            key={`${s.name}-${i}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-slate-200 text-[11px] font-semibold text-slate-700 whitespace-nowrap shadow-sm"
                        >
                            {s.domain && (
                                <img
                                    src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(s.domain)}&sz=32`}
                                    alt=""
                                    className="h-3.5 w-3.5"
                                    loading="lazy"
                                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                                />
                            )}
                            {s.name}
                        </span>
                    ))}
                </div>
            </div>
            {/* Marquee keyframes are scoped inline so the ticker is fully
                self-contained and survives any global stylesheet shuffles. */}
            <style>{`
                @keyframes news-ticker-scroll {
                    from { transform: translateX(0); }
                    to   { transform: translateX(-50%); }
                }
                .animate-news-ticker {
                    animation: news-ticker-scroll 45s linear infinite;
                }
            `}</style>
        </div>
    );
};

/**
 * SharedArticleHero — pinned at the top when a user lands via a
 * shared `/news?a=<token>` deeplink. Gives the recipient the article
 * context, source, and a clear "Open article" CTA WITHOUT yanking
 * them out of Odyssey. The "Dismiss" pill drops the query param so
 * the regular feed slides back to the top.
 */
const SharedArticleHero = ({ item, onDismiss, onShare }) => {
    const fav = item.publisher_domain
        ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(item.publisher_domain)}&sz=128`
        : "";
    return (
        <section
            data-testid="news-shared-hero"
            className="relative overflow-hidden rounded-2xl border-2 border-[#2EB4AC]/40 bg-gradient-to-br from-teal-50 via-white to-indigo-50 p-5 sm:p-6 shadow-sm"
        >
            <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-[#2EB4AC]/15 blur-3xl pointer-events-none" />
            <div className="flex items-start gap-2 flex-wrap mb-3">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#2EB4AC] text-white text-[10px] font-bold uppercase tracking-[0.16em]">
                    <Share2 className="h-3 w-3" /> Shared with you
                </span>
                <button
                    onClick={onDismiss}
                    data-testid="news-shared-dismiss"
                    className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-slate-500 hover:text-slate-900"
                >
                    <X className="h-3 w-3" /> Dismiss
                </button>
            </div>
            <div className="flex gap-4 sm:gap-5">
                <div className="relative h-24 w-24 sm:h-32 sm:w-32 rounded-xl overflow-hidden shrink-0 bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center">
                    {item.image ? (
                        <img src={item.image} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
                    ) : fav ? (
                        <img src={fav} alt="" loading="lazy" className="h-10 w-10 sm:h-12 sm:w-12 opacity-90" />
                    ) : (
                        <Newspaper className="h-8 w-8 text-slate-500" />
                    )}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#25908A] font-bold truncate">
                        {item.source}
                        {item.published && <span className="text-slate-400 normal-case tracking-normal font-medium ml-1.5">· {relativeTime(item.published)}</span>}
                    </div>
                    <h2 className="font-heading text-lg sm:text-2xl font-bold text-slate-900 leading-tight mt-1 line-clamp-3">
                        {item.title}
                    </h2>
                    <div className="mt-4 flex items-center gap-2 flex-wrap">
                        <a
                            href={item.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => trackArticleView(item)}
                            data-testid="news-shared-open"
                            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-slate-900 hover:bg-slate-700 text-white text-sm font-semibold transition-colors"
                        >
                            Open article <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                        <button
                            onClick={() => (onShare ? onShare() : shareArticle(item))}
                            data-testid="news-shared-reshare"
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 hover:border-[#2EB4AC] text-sm font-semibold text-slate-700 hover:text-[#25908A] transition-colors"
                        >
                            <Share2 className="h-3.5 w-3.5" /> Re-share
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
};


/**
 * ShareDialog — modal popup shown when a user taps the share icon on
 * any news card. Exposes:
 *   - Copy link (Odyssey-hosted deeplink → tracked re-engagement)
 *   - Share to apps (Web Share API — only when the browser supports it)
 *   - One-tap brand shortcuts: WhatsApp, LinkedIn, X, Email
 * Each brand intent opens in a new tab with the deeplink + a short
 * default message so the user only has to pick a destination + send.
 */
const ShareDialog = ({ item, onClose }) => {
    const dialogRef = useRef(null);
    // Start with the legacy long URL so Copy works even if the short-code
    // request hasn't returned yet, then swap once the 7-char code lands.
    const [shareUrl, setShareUrl] = useState(() => {
        const cached = _shortCodeCache.get(item.link);
        return cached ? buildShortShareUrl(cached) : buildShareUrl(item.link);
    });
    const [shortening, setShortening] = useState(() => !_shortCodeCache.has(item.link));
    const title = item.title || "Worth a read";
    const text = `${item.source ? `${item.source} · ` : ""}${title}`;
    const composed = `${text}\n${shareUrl}`;

    // Request a short code on mount. Idempotent server-side, so re-opens
    // for the same link don't re-write the DB.
    useEffect(() => {
        let alive = true;
        (async () => {
            const code = await fetchOrCreateShortCode(item);
            if (!alive) return;
            if (code) setShareUrl(buildShortShareUrl(code));
            setShortening(false);
        })();
        return () => { alive = false; };
    }, [item.link]);

    // Close on outside-click / Escape — basic dialog hygiene
    useEffect(() => {
        const onDoc = (e) => {
            if (dialogRef.current && !dialogRef.current.contains(e.target)) onClose?.();
        };
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [onClose]);

    const intents = [
        {
            key: "whatsapp",
            label: "WhatsApp",
            href: `https://wa.me/?text=${encodeURIComponent(composed)}`,
            Icon: MessageCircle,
            tint: "bg-emerald-50/70 text-emerald-700 ring-emerald-200/70 hover:bg-emerald-100/80",
        },
        {
            key: "linkedin",
            label: "LinkedIn",
            href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
            Icon: Linkedin,
            tint: "bg-sky-50/70 text-sky-700 ring-sky-200/70 hover:bg-sky-100/80",
        },
        {
            key: "x",
            label: "X (Twitter)",
            href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(title)}`,
            // Lucide doesn't ship an X glyph — render the brand mark inline
            Icon: null,
            tint: "bg-slate-900/90 text-white ring-slate-900/40 hover:bg-slate-900",
        },
        {
            key: "email",
            label: "Email",
            href: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${text}\n\n${shareUrl}`)}`,
            Icon: Mail,
            tint: "bg-rose-50/70 text-rose-700 ring-rose-200/70 hover:bg-rose-100/80",
        },
    ];

    // Fire a track ping *only* when the user actually performs a share
    // action (Copy or an intent tile). Awarded XP is one-shot per user ×
    // article on the backend, so we can call this liberally.
    const trackIntent = async (intent) => {
        if (!item?.link) return;
        try {
            const code = _shortCodeCache.get(item.link);
            if (!code) return;  // dialog opened but code not yet minted — skip silently
            const r = await api.post(`/news/share/${code}/track`, { intent });
            if (r.data?.xp_awarded > 0) {
                toast.success(`+${r.data.xp_awarded} XP — thanks for sharing!`);
            }
        } catch { /* non-blocking */ }
    };

    const handleCopy = async () => {
        const ok = await copyToClipboard(shareUrl);
        if (ok) toast.success("Odyssey link copied");
        else {
            try { window.prompt("Copy this Odyssey share link:", shareUrl); }
            catch { toast.error("Couldn't copy — try right-click → Copy"); }
        }
        trackIntent("copy");
        onClose?.();
    };

    const handleNativeShare = async () => {
        if (!navigator.share) return;
        try {
            await navigator.share({ title, text, url: shareUrl });
            trackIntent("native");
            onClose?.();
        } catch (e) {
            if (e?.name !== "AbortError") toast.error("Share failed");
        }
    };

    return createPortal(
        <div
            // Centred on the actual viewport regardless of how far the
            // user has scrolled — `fixed` + `inset-0` + body-portal
            // mount means even a parent `transform` / `filter` ancestor
            // can't reframe us as its containing block.
            // Light, blurry overlay — keeps focus on the modal but lets
            // the underlying news content shine through so the panel reads
            // as glass, not as an opaque card.
            className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-slate-950/25 backdrop-blur-md"
            data-testid="news-share-dialog"
        >
            <div
                ref={dialogRef}
                className="relative w-full max-w-lg animate-[news-share-pop_180ms_ease-out]"
            >
                <div className="relative bg-white/30 backdrop-blur-3xl backdrop-saturate-200 rounded-2xl border border-white/50 ring-1 ring-white/30 overflow-hidden shadow-[0_30px_80px_-20px_rgba(15,23,42,0.45)]">
                {/* Top-edge sheen — fakes the catch-light on a real glass
                    panel; one of the most readable glass-morphism cues. */}
                <span aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/80 to-transparent" />
                <span aria-hidden className="pointer-events-none absolute -top-1/2 -left-1/2 h-full w-full rounded-full bg-white/20 blur-3xl" />
                {/* Header — article preview so the user remembers what
                    they're sharing if they paused before clicking send */}
                <div className="relative px-6 pt-5 pb-4 border-b border-white/40">
                    <div className="flex items-start justify-between gap-3 mb-3">
                        <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold text-[#1F7F79]">
                            <Share2 className="h-3.5 w-3.5" /> Share article
                        </span>
                        <button
                            onClick={onClose}
                            aria-label="Close"
                            className="text-slate-600 hover:text-slate-900 -mr-1 transition-colors"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                    <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-700 truncate">
                        {item.source}{item.published && <span className="text-slate-500 font-normal normal-case tracking-normal"> · {relativeTime(item.published)}</span>}
                    </div>
                    <div className="text-base font-bold text-slate-900 line-clamp-2 mt-1.5 leading-snug">
                        {title}
                    </div>
                </div>

                {/* Copy link — the headline action, large + primary */}
                <div className="relative p-6 space-y-4">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-700 mb-2 flex items-center gap-2">
                            <span>Share link</span>
                            {shortening && <span className="normal-case tracking-normal text-[10px] text-slate-500 font-normal">shortening…</span>}
                        </div>
                        <div className="flex items-center gap-2 p-2 pl-3 bg-white/30 backdrop-blur-md border border-white/50 rounded-lg ring-1 ring-white/30">
                            <Link2 className="h-4 w-4 text-[#2EB4AC] shrink-0" />
                            <span className="text-xs font-mono text-slate-800 truncate flex-1" title={shareUrl}>
                                {shareUrl}
                            </span>
                            <button
                                onClick={handleCopy}
                                data-testid="news-share-copy"
                                disabled={shortening}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#2EB4AC] hover:bg-[#25908A] text-white text-xs font-bold transition-colors shadow-[0_4px_12px_-2px_rgba(46,180,172,0.5)] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Copy
                            </button>
                        </div>
                    </div>

                    {/* Brand row */}
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-700 mb-2">Share to</div>
                        <div className="grid grid-cols-4 gap-2.5">
                            {intents.map(({ key, label, href, Icon, tint }) => (
                                <a
                                    key={key}
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`news-share-${key}`}
                                    onClick={() => { trackIntent(key); onClose(); }}
                                    className={`flex flex-col items-center justify-center gap-1.5 py-3.5 rounded-xl ring-1 backdrop-blur-md transition-all hover:-translate-y-0.5 hover:shadow-lg ${tint}`}
                                >
                                    {Icon ? <Icon className="h-5 w-5" /> : (
                                        // X / Twitter brand mark (Lucide ships no X glyph)
                                        <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden>
                                            <path d="M18.244 2H21.5l-7.5 8.57L23 22h-6.84l-5.36-7.06L4.6 22H1.34l8.05-9.19L1 2h6.84l4.86 6.43L18.244 2Zm-1.2 18h1.88L7.07 4H5.12l11.924 16Z" />
                                        </svg>
                                    )}
                                    <span className="text-[10px] font-semibold uppercase tracking-[0.08em]">{label}</span>
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Native share fallback (Web Share API) — only on
                        browsers that expose it (mostly mobile). */}
                    {typeof navigator !== "undefined" && navigator.share && (
                        <button
                            onClick={handleNativeShare}
                            data-testid="news-share-native"
                            className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-lg bg-white/30 hover:bg-white/50 backdrop-blur-md border border-white/50 ring-1 ring-white/30 text-slate-800 text-sm font-semibold transition-colors"
                        >
                            <Share2 className="h-4 w-4" /> More apps…
                        </button>
                    )}

                    <p className="text-[11px] text-slate-600 text-center pt-1">
                        Links route through Odyssey so we can keep your team in the loop.
                    </p>
                </div>
                </div>
            </div>
            <style>{`
                @keyframes news-share-pop {
                    from { opacity: 0; transform: scale(0.96) translateY(6px); }
                    to   { opacity: 1; transform: scale(1) translateY(0); }
                }
            `}</style>
        </div>,
        document.body
    );
};

/**
 * ViewersDialog — opened from the "Read by N" pill on every news card.
 * Shows the total reader count + the full list of teammates who opened
 * the article (name + last-opened relative time + per-user view count).
 * Visible to *everyone* — it's a team-engagement signal, not a privacy
 * concern. Portal-mounted for proper viewport centring.
 */
const ViewersDialog = ({ item, onClose }) => {
    const dialogRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(item.view_count || 0);
    const [viewers, setViewers] = useState([]);
    const [error, setError] = useState("");

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await api.get("/news/views/article", { params: { link: item.link } });
                if (!alive) return;
                setTotal(r.data?.total_viewers ?? 0);
                setViewers(r.data?.viewers || []);
            } catch (e) {
                if (!alive) return;
                setError(e?.response?.data?.detail || "Couldn't load viewers");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [item.link]);

    useEffect(() => {
        const onDoc = (e) => {
            if (dialogRef.current && !dialogRef.current.contains(e.target)) onClose?.();
        };
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [onClose]);

    const initials = (name) => (name || "?")
        .split(" ").filter(Boolean).slice(0, 2)
        .map((s) => s[0].toUpperCase()).join("");

    // Color palette for avatars — deterministic per user_id so the same
    // teammate always gets the same hue across sessions.
    const avatarTints = [
        "from-teal-500 to-emerald-600",
        "from-sky-500 to-indigo-600",
        "from-violet-500 to-fuchsia-600",
        "from-amber-500 to-orange-600",
        "from-rose-500 to-pink-600",
        "from-cyan-500 to-blue-600",
    ];
    const tintFor = (id) => {
        const s = String(id || "");
        let h = 0;
        for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
        return avatarTints[Math.abs(h) % avatarTints.length];
    };

    return createPortal(
        <div
            className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-slate-950/25 backdrop-blur-md"
            data-testid="news-viewers-dialog"
        >
            <div ref={dialogRef} className="relative w-full max-w-lg animate-[news-share-pop_180ms_ease-out]">
                <div className="relative bg-white/30 backdrop-blur-3xl backdrop-saturate-200 rounded-2xl border border-white/50 ring-1 ring-white/30 overflow-hidden shadow-[0_30px_80px_-20px_rgba(15,23,42,0.45)]">
                    {/* Glass top sheen */}
                    <span aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/80 to-transparent" />
                    <span aria-hidden className="pointer-events-none absolute -top-1/2 -left-1/2 h-full w-full rounded-full bg-white/20 blur-3xl" />
                    <div className="relative px-6 pt-5 pb-4 border-b border-white/40">
                        <div className="flex items-start justify-between gap-3 mb-3">
                            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold text-[#1F7F79]">
                                <Users className="h-3.5 w-3.5" /> Who has read this
                            </span>
                            <button onClick={onClose} aria-label="Close" className="text-slate-600 hover:text-slate-900 -mr-1 transition-colors">
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-700 truncate">
                            {item.source}{item.published && <span className="text-slate-500 font-normal normal-case tracking-normal"> · {relativeTime(item.published)}</span>}
                        </div>
                        <div className="text-base font-bold text-slate-900 line-clamp-2 mt-1.5 leading-snug">{item.title}</div>
                    </div>

                    <div className="relative p-6">
                        <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/40">
                            <div>
                                <div className="text-[11px] uppercase tracking-[0.14em] text-slate-700 font-semibold">
                                    Total readers
                                </div>
                                <div className="text-xs text-slate-600 mt-0.5">Teammates who opened this article</div>
                            </div>
                            <div className="text-3xl font-heading font-extrabold text-slate-900 tabular-nums" data-testid="news-viewers-total">
                                {total}
                            </div>
                        </div>

                        {loading ? (
                            <div className="space-y-2">
                                {[0, 1, 2, 3].map((i) => <div key={i} className="h-14 rounded-lg bg-white/30 backdrop-blur-md animate-pulse" />)}
                            </div>
                        ) : error ? (
                            <div className="text-sm text-rose-700 bg-rose-50/60 backdrop-blur-md border border-rose-200/60 rounded-lg p-3 text-center">
                                {error}
                            </div>
                        ) : viewers.length === 0 ? (
                            <div className="text-center py-8">
                                <Eye className="h-8 w-8 text-slate-500 mx-auto mb-2" />
                                <div className="text-sm font-semibold text-slate-800">
                                    No-one&apos;s opened this yet
                                </div>
                                <div className="text-xs text-slate-600 mt-1">
                                    Be the first to click Read.
                                </div>
                            </div>
                        ) : (
                            <ul className="space-y-2 max-h-80 overflow-y-auto pr-1 -mr-1" data-testid="news-viewers-list">
                                {viewers.map((v) => (
                                    <li key={v.user_id} className="flex items-center gap-3 p-3 rounded-lg bg-white/30 hover:bg-white/50 backdrop-blur-md border border-white/50 ring-1 ring-white/30 transition-colors">
                                        <span className={`h-10 w-10 rounded-full bg-gradient-to-br ${tintFor(v.user_id)} inline-flex items-center justify-center text-white text-sm font-bold shrink-0 shadow-md ring-2 ring-white/50`}>
                                            {initials(v.user_name)}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-semibold text-slate-900 truncate">{v.user_name || v.user_email || v.user_id}</div>
                                            <div className="text-xs text-slate-700 truncate">
                                                {relativeTime(v.last_viewed_at)}
                                                {v.view_count > 1 && <span className="ml-1.5 text-[#1F7F79] font-semibold">· {v.view_count}× opens</span>}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

